/* THE MAPPER — the app's holding shape ⇄ a `holdings` row
 *
 * PHASE 3, STEP 3. Like `src/idb.js` and `src/auth.js`, this file is NEW
 * CODE rather than moved code: `scripts/extract.mjs` does not generate
 * it. Everything else in src/ except those two is regenerated from the
 * HTML source, so a hand edit there is silently lost — see BUILD_NOTES.
 *
 * ── WHAT THIS IS FOR ───────────────────────────────────────────────
 * The app's model is ONE JSON STRING holding the entire vault:
 *
 *   set("notevault:collection", JSON.stringify(state.collection))
 *
 * `holdings` is a table of rows with typed columns. Neither side is
 * wrong; they simply do not have the same shape, and every field that
 * crosses between them is a chance to lose something quietly. Step 2
 * found five fields with nowhere to land by reading one shape against
 * the other — this file is where that mapping now lives, in one place,
 * so it can be read and so it can be tested.
 *
 * PURE, AND DELIBERATELY SO. No network, no session. Every function
 * here is a total function of its arguments, which is what lets
 * `npm run roundtrip-check` import THE REAL MODULE — not a copy — and
 * check it against the live database. Same rule as the R2 signer in
 * Phase 2 Step 4.
 *
 * ⚠ IT NOW HAS EXACTLY ONE IMPORT, WHERE IT PREVIOUSLY CLAIMED NONE.
 * `shared/photo-ref.js`, added in Phase 4 Step 3 so the ref grammar
 * stops being written down twice. It is itself pure and imports
 * nothing, so the purity that makes this file testable is unchanged —
 * but the claim above said "no imports", and a claim that quietly
 * stops being true is worse than one that was never made.
 *
 * ── THE ELEVEN FIELDS ──────────────────────────────────────────────
 * Confirmed exhaustively in Step 2 by grepping every assignment, not by
 * reading: id, catId, grade, cost, appr, acq, memo, ref, year, hasFront,
 * hasBack.
 *
 *   h.id        → id uuid          ⚠ see NOT A UUID below
 *   h.catId     → catalog_sid      rename
 *   h.grade     → grade            rename
 *   h.cost      → cost numeric     "" → NULL, never 0
 *   h.appr      → user_value       "" → NULL, never 0
 *   h.acq       → acquired_on date "" → NULL
 *   h.memo      → personal_note    rename
 *   h.ref       → reference_no     PRIVATE — not in public_holdings
 *   h.year      → series_year      PUBLIC — in public_holdings
 *   h.hasFront  → photo_front_ref  `idb:<id>:front`, or NULL
 *   h.hasBack   → photo_back_ref   `idb:<id>:back`, or NULL
 *   —           → quantity         written as 1, ignored on read
 *   —           → user_id          NOT WRITTEN HERE. See below.
 *
 * ── THIS MAPPER NEVER NAMES THE OWNER ──────────────────────────────
 * `holdingToRow()` emits no `user_id`, and that is a design decision
 * rather than an omission. The owner comes from `auth.uid()` inside the
 * `sync_holdings` RPC — a value the client cannot supply, forge, or get
 * wrong. A mapper that took a userId argument would be one bad call
 * site away from writing a row into somebody else's vault, and would
 * make "whose row is this?" a question about application code, which
 * safety rule 4 exists to avoid.
 *
 * RLS is still the boundary underneath. This just means the client never
 * gets to have an opinion about the answer.
 *
 * ── EMPTY STRING BECOMES NULL, EVERYWHERE, ON PURPOSE ──────────────
 * The app writes "" for every unset field; the schema is nullable. One
 * rule is applied uniformly: "" ⇄ NULL, in both directions.
 *
 * The consequence, stated rather than discovered later: "" and NULL are
 * INDISTINGUISHABLE after a round trip. That is correct here — the app
 * has no notion of "explicitly empty" as opposed to "not set" — but it
 * would not be correct for a schema that did.
 *
 * ⚠ AND IT IS "" → NULL, NEVER "" → 0. Step 2's decision, and for
 * `user_value` it is a data-corruption path rather than a preference:
 * state.js:21 reads "" as "no appraisal — use the catalog value" and 0
 * as "I appraise this at $0". Mapping "" to 0 would silently override
 * the catalog value with zero on every holding that was never
 * appraised, and collapse the vault total. NULL is "I haven't recorded
 * what I paid"; 0 is "it was free".
 *
 * ── IT REFUSES RATHER THAN GUESSES ─────────────────────────────────
 * `holdingToRow` THROWS on a value it cannot represent instead of
 * substituting a plausible one. The trade-off is real and is taken with
 * open eyes: one unmappable holding stops the WHOLE vault from being
 * written, because the sync is a single all-or-nothing call.
 *
 * It is taken because every input this can reject is already impossible
 * through the UI — the cost and appraisal fields strip to [0-9], the
 * year field to [0-9]{0,4}, and catId is always assigned from a catalog
 * entry. So a throw here does not mean "the user typed something odd",
 * it means the stored vault is not the shape this app writes, and
 * silently dropping a field would be the worse answer to that.
 *
 * ── NOT A UUID: THE ONE MIGRATION THIS FILE DOES NOT DO ────────────
 * The app generates `"h" + Date.now()` (custom.js:30,49). Step 2 decided
 * holdings get a real `crypto.randomUUID()` — "h1754..." is not a uuid,
 * and it collides for two notes added inside one millisecond, which
 * Phase 3 makes reachable by giving two devices one vault.
 *
 * `holdingToRow` REFUSES a non-uuid id, loudly, naming the fix. It does
 * not rewrite it, because rewriting an id is not a mapping — it is a
 * migration, and it has to move the photo keys with it
 * (`notevault:photo:<id>:<slot>`) or every photo in the vault is
 * orphaned. That belongs to the backend, in Step 4. See BUILD_NOTES.
 */

import { idbRef, parsePhotoRef } from '../shared/photo-ref.js'

/* ── custom (quick-add) catalog entries ────────────────────────────── */

/*
 * ⚠ THE SECOND STORE, AND IT IS EASY TO FORGET THERE IS ONE.
 *
 * Quick-add (custom.js:26-34) writes TWO things: a holding, and a
 * catalog entry for it in a separate blob, `notevault:customnotes`. The
 * holding's `catId` is the entry's `sid` — `CUSTOM:<ts><rand>` — and
 * nothing else in the app can resolve it.
 *
 * So a swap that syncs the collection and leaves the entries behind
 * does NOT fail. It is worse than that: on a second device `BY_SID` has
 * no such sid, `cat(h.catId)` is undefined, and BOTH renderers drop the
 * holding on the floor — `views-vault.js:26` and `views-community.js:34`
 * are each `.filter(o => o.n)`. The collector's note does not error, it
 * VANISHES, on one device and not the other.
 *
 * Jesse's call, 2026-08-08: the entry rides ON the holding, in a
 * `custom_entry jsonb` column. One migration, no second table, and one
 * holding stays one row — so a holding and the only thing that can
 * render it can never again be written apart or read apart.
 *
 * ⚠ PRIVATE. It is deliberately absent from `public_holdings`, and that
 * PRESERVES today's behaviour rather than changing it: a stranger's
 * BY_SID never held another collector's CUSTOM sid either, so custom
 * notes do not render on a public profile today. Same reasoning as
 * `reference_no` in Step 2. Making them public is a Step 6 decision
 * about the Community tab, not a side effect of fixing a data loss.
 */
const CUSTOM_SID_PREFIX = 'CUSTOM:'

/** True for a holding whose catalog entry is user-created, not Friedberg. */
function isCustomSid(sid) {
  return typeof sid === 'string' && sid.startsWith(CUSTOM_SID_PREFIX)
}

/* `sid` and `cust` are NOT stored. `sid` would be a second copy of
 * `catalog_sid` — two places to hold one fact, and one of them free to
 * drift. `cust: true` is a type tag that is true of every entry in this
 * column by definition. Both are reconstructed on the way back, so the
 * blob holds only what cannot be derived.
 *
 * Everything else is stored VERBATIM, including `g` and `gl`, which are
 * constants today (7 nulls and the standard grade list). Storing them
 * costs nothing and avoids this mapper making a claim about which of
 * the app's fields are constant — a claim that would silently rot the
 * day one of them stops being. `g` in particular is load-bearing:
 * bookVal() reads `n.g[i]` and views-vault.js:132 reads
 * `n.g[gs.indexOf(g)]`, so an entry that came back without it would
 * throw during a render rather than degrade. */
function customEntryToJson(entry) {
  if (!entry || typeof entry !== 'object') return null
  const { sid, cust, ...rest } = entry
  return rest
}

function jsonToCustomEntry(json, sid) {
  if (!json || typeof json !== 'object') return null
  return { ...json, sid, cust: true }
}

/* ── ids ───────────────────────────────────────────────────────────── */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** True for a canonical hyphenated UUID, in either case. */
function isUuid(v) {
  return typeof v === 'string' && UUID_RE.test(v)
}

/**
 * A new holding id.
 *
 * Client-generated rather than left to the column default, because
 * custom.js:33,50 set `state.sel = h.id` immediately after the push and
 * before any write lands — so the app needs the id before the database
 * has seen the row.
 */
function newHoldingId() {
  return globalThis.crypto.randomUUID()
}

/* ── photo refs ────────────────────────────────────────────────────── */

/* Photos do NOT move to R2 in Phase 3: /api/photos/upload is 501 by
 * design (the tier encoder is Phase 4's second runtime), and Phase 2
 * Step 4 deliberately refused to mint signed URLs for caller-supplied
 * keys.
 *
 * ⚠ CORRECTED IN PHASE 4 STEP 3. This paragraph used to add "and the
 * userId that names the object key comes from Phase 5". It does not, and
 * it never needed to: `_lib/photo-upload.js` reads it off the holding
 * row through PostgREST with the caller's own token. That was the
 * SEVENTH place in this tree carrying the stale claim, and the last —
 * the other six were corrected in the Step 3 commit.
 *
 * So the bytes stay in IndexedDB and the COLUMN carries a real ref to
 * where they are, rather than the schema growing has_front/has_back
 * booleans. Two reasons: hasFront/hasBack must survive a reload once
 * the collection comes from Postgres rather than from a local blob, and
 * a ref makes Phase 4 a VALUE REWRITE (`idb:` → `r2:`) instead of a
 * schema migration. One column keeps doing one job. */
/* ⚠ THE GRAMMAR MOVED OUT OF THIS FILE — Phase 4 Step 3, closing the
 * carry-forward that said the ref grammar was defined in two places.
 * `shared/photo-ref.js` is now the only definition, imported here and
 * by `functions/_lib/photo-delivery.js`.
 *
 * ⚠ AND THE TWO DEFINITIONS HAD ALREADY DISAGREED. What used to sit
 * here required exactly three colon-separated parts, because
 * `idb:<holdingId>:<slot>` was all anything produced — so it read
 * `r2:<hash>`, which has two, as NULL. Null here means "no photo", not
 * "unreadable", so every migrated photograph would have rendered as an
 * empty slot with nothing reported. It never fired only because this
 * parser had no callers. See shared/photo-ref.js for the measurement.
 *
 * `photoRef(id, slot)` is kept as a NAME because `holdingToRow` below
 * calls it, but it now delegates: one grammar, one place it can change. */
function photoRef(id, slot) {
  return idbRef(id, slot)
}

/* ── ⚠⚠⚠ THE REF A ROW ALREADY CARRIES SURVIVES THE ROUND TRIP ───────
 *        PHASE 4, STEP 7, ACT 2's PREREQUISITE. MEASURED 2026-08-24.
 *
 * `rowToHolding` used to reduce a ref to a BOOLEAN (`hasFront`), and
 * `holdingToRow` then MINTED A FRESH `idb:` REF from that boolean. So a
 * migrated row made exactly one round trip through this file before the
 * migration was undone:
 *
 *   DB after Act 2   photo_front_ref = r2:e2c5ae32…
 *   rowToHolding     { hasFront: true }              ← the ref is dropped
 *   holdingToRow     photo_front_ref = idb:<id>:front ← and re-invented
 *
 * ⚠⚠ AND `sync_holdings` IS A FULL REPLACE, so the NEXT ordinary save —
 * adding a note, a `filters.js` year backfill, anything that calls
 * `save()` — writes that back over all seven rows at once.
 *
 * ⚠⚠⚠ THE REASON THIS HAD TO BE FOUND BEFORE ACT 2 RATHER THAN AFTER IS
 * THAT IT IS COMPLETELY SILENT. The bytes stay in R2, the row says
 * `idb:`, the app renders from the local preview it still has, and
 * `photo-census` reports 7 idb / 0 r2 — the state it reports if Act 2
 * had never been run at all. Nothing is logged, nothing 500s, and the
 * only symptom is a migration that did not stick.
 *
 * ── WHAT THIS FUNCTION DOES, AND WHAT IT DELIBERATELY DOES NOT ───────
 * An `r2:` ref means the bytes are in the bucket and this client did not
 * put them there — so it is not this client's to overwrite. Anything
 * else mints `idb:<id>:<slot>` exactly as before.
 *
 * ⚠ EVERY EXISTING CALLER IS UNAFFECTED. A holding with no carried ref —
 * which is every holding built by any code path older than this comment
 * — takes the `photoRef` branch, which is the line that was here before.
 * The change is additive: it can only preserve a ref that already
 * existed in the column.
 *
 * ⚠ AND A LOCAL REPLACE MUST CLEAR IT, which is why `photos.js` nulls
 * the carried ref when it writes a new local photo. A collector who
 * replaces a migrated photograph has bytes in their browser that are NOT
 * the ones in the bucket, and preserving `r2:<oldhash>` there would
 * point the row at the PREVIOUS photograph — the row and the picture
 * disagreeing, silently, which is the whole failure this guards. */
function refOut(h, slot, id) {
  const has = slot === 'front' ? h.hasFront : h.hasBack
  if (!has) return null

  const carried = slot === 'front' ? h.frontRef : h.backRef
  if (typeof carried === 'string' && parsePhotoRef(carried).scheme === 'r2') return carried

  /* `idb:`, malformed, absent — all mint a fresh ref, which is both the
   * historical behaviour and a normalisation of a malformed value. */
  return photoRef(id, slot)
}

/* ── scalars ───────────────────────────────────────────────────────── */

/** "" | null | undefined → null. Everything else → a trimmed string. */
function textOut(v) {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  return s === '' ? null : s
}

/** NULL → "". The other direction of the one rule. */
function textIn(v) {
  return v === null || v === undefined ? '' : String(v)
}

/**
 * "" → NULL, "1200" → 1200, "0" → 0.
 *
 * ⚠ Throws rather than coercing. `Number("abc")` is NaN and
 * `JSON.stringify(NaN)` is `null` — so without this guard a corrupted
 * value would arrive at Postgres as a perfectly valid NULL and the
 * money would be gone with nothing anywhere reporting it.
 */
function moneyOut(v, field, id) {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (s === '') return null
  const n = Number(s)
  if (!Number.isFinite(n)) {
    throw new Error(`holding ${id}: ${field} is not a number (${JSON.stringify(v)})`)
  }
  return n
}

/**
 * NULL → "", 1200 → "1200", 0 → "0".
 *
 * A string, because that is what the app holds: main.js:58,61 assign
 * `e.target.value.replace(/[^0-9]/g,"")`, and state.js:21 compares
 * against "" by identity. Handing back a number would make `h.appr === ""`
 * false for a value the app itself would have stored as "".
 *
 * The client is digits-only, so these are integers and the trip through
 * `numeric` is exact. A decimal would round-trip through JSON as a JS
 * number and could lose its trailing zero ("12.50" → "12.5"); there is
 * no path to one today, and this comment is here so that stays true.
 */
function moneyIn(v) {
  return v === null || v === undefined ? '' : String(v)
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** "" → NULL. A date input can be cleared (main.js:62), so this is reachable. */
function dateOut(v, id) {
  const s = textOut(v)
  if (s === null) return null
  if (!DATE_RE.test(s)) {
    throw new Error(`holding ${id}: acq is not YYYY-MM-DD (${JSON.stringify(v)})`)
  }
  return s
}

/* ── holdings ──────────────────────────────────────────────────────── */

/** Every key `rowToHolding` produces. The canonical app-side shape. */
const HOLDING_KEYS = [
  'id', 'catId', 'grade', 'cost', 'appr', 'acq', 'memo', 'ref', 'year', 'hasFront', 'hasBack',
  /* ⚠ THIRTEEN SINCE 2026-08-24, NOT ELEVEN. Added by Act 2's
   * prerequisite so a migrated `r2:` ref survives the round trip — see
   * `refOut`. The eleven above are unchanged and in their original
   * order; these two are additive, always present, and never
   * `undefined`, which is the property `roundtrip-check` asserts. */
  'frontRef', 'backRef',
  /* ⚠⚠ FOURTEEN SINCE 2026-09-21, NOT THIRTEEN. `qualifier` is the
   * EPQ/PPQ designation, which lived inside `memo` from step 3.5 until
   * his ruling of 2026-09-21 gave it its own column (db/012) — the one
   * field his "NO new columns" decision of 2026-09-13 is reversed for,
   * because the letters were duplicating text the collector owns.
   * ⚠ Always present and never `undefined`, which is the property
   * `roundtrip-check` asserts over this whole list. */
  'qualifier',
]

/**
 * One app holding → one `holdings` row, ready for `sync_holdings`.
 *
 * No `user_id` and no `created_at`: the first belongs to auth.uid(), the
 * second to the database. Sending either would be the client having an
 * opinion about something it does not own.
 */
function holdingToRow(h, customEntry) {
  if (!h || typeof h !== 'object') throw new Error('holdingToRow: not a holding')

  const id = h.id
  if (!isUuid(id)) {
    throw new Error(
      `holding id ${JSON.stringify(id)} is not a uuid — legacy "h<timestamp>" ids must be ` +
      'migrated (with their photo keys) before the vault can sync. See BUILD_NOTES, Phase 3 Step 4.'
    )
  }

  const catId = textOut(h.catId)
  if (catId === null) {
    throw new Error(`holding ${id}: catId is empty — a holding must reference a catalog entry`)
  }

  /* The vanishing-note bug, refused at the boundary. A custom holding
   * whose entry did not come along is a note that will render nowhere
   * and be silently filtered out of the vault — so it is not written at
   * all rather than written half-way. */
  const custom = isCustomSid(catId)
  if (custom && !customEntry) {
    throw new Error(
      `holding ${id}: catId ${catId} is a quick-add entry but none was supplied — ` +
      'the note would be dropped by views-vault.js:26 on any other device'
    )
  }
  if (!custom && customEntry) {
    throw new Error(`holding ${id}: catId ${catId} is a catalog sid, so it must not carry a custom entry`)
  }

  return {
    id,
    catalog_sid: catId,
    grade: textOut(h.grade),
    cost: moneyOut(h.cost, 'cost', id),
    user_value: moneyOut(h.appr, 'appr', id),
    /* The app has no concept of quantity. Written as 1 because the column
     * is NOT NULL, and ignored on the way back — recorded so it is not
     * later mistaken for a dropped feature. */
    quantity: 1,
    personal_note: textOut(h.memo),
    acquired_on: dateOut(h.acq, id),
    photo_front_ref: refOut(h, 'front', id),
    photo_back_ref: refOut(h, 'back', id),
    series_year: textOut(h.year),
    reference_no: textOut(h.ref),
    custom_entry: custom ? customEntryToJson(customEntry) : null,
    /* ⚠⚠⚠ A BOOLEAN, COERCED, AND NOT `textOut`. `db/012`'s column is
     * `not null`, and `sync_holdings` v3 refuses any field it does not
     * name — so this key MUST be sent once the column exists and MUST
     * be a real boolean. `!!` rather than a pass-through because a
     * holding read from an older client's storage carries `undefined`
     * here, and `undefined` would be dropped by JSON.stringify and
     * arrive as NULL. The server coalesces NULL to false, so nothing
     * breaks either way — but sending the value the app actually holds
     * is what makes the round trip assertable. */
    qualifier: !!h.qualifier,
  }
}

/**
 * One `holdings` row → one app holding, in CANONICAL form: always all
 * eleven keys, never `undefined`.
 *
 * ⚠ `year` MUST come back as "" rather than be omitted. filters.js:22
 * treats `h.year === undefined` as "needs backfilling" and calls save()
 * — so a mapper that dropped the key would fire a whole-vault write on
 * every single load, forever, and the write would look like the user's.
 */
function rowToHolding(row) {
  if (!row || typeof row !== 'object') throw new Error('rowToHolding: not a row')
  return {
    id: row.id,
    catId: textIn(row.catalog_sid),
    grade: textIn(row.grade),
    cost: moneyIn(row.cost),
    appr: moneyIn(row.user_value),
    acq: textIn(row.acquired_on),
    memo: textIn(row.personal_note),
    ref: textIn(row.reference_no),
    year: textIn(row.series_year),
    hasFront: row.photo_front_ref != null,
    hasBack: row.photo_back_ref != null,
    /* ⚠ THE REF ITSELF, CARRIED RATHER THAN REDUCED — see `refOut`.
     * `hasFront` stays exactly what it was and every reader of it is
     * untouched; these two keys are what stop `holdingToRow` having to
     * re-invent a value it was given. Null for an empty slot, so the
     * canonical form still never holds `undefined`. */
    frontRef: row.photo_front_ref ?? null,
    backRef: row.photo_back_ref ?? null,
    /* ⚠ `!!` again, for the mirror reason: a row written before db/012
     * has no such key and a row written after it always does. Either
     * way the canonical form holds a boolean and never `undefined`. */
    qualifier: !!row.qualifier,
  }
}

/**
 * The canonical form of an app holding: what it becomes after one round
 * trip. Pure, so a caller can compare against it without a database.
 *
 * `rowToHolding(holdingToRow(h))` is idempotent — applying it twice
 * changes nothing — which is what makes "the round trip did not alter
 * the vault" a statement with a fixed point to check against.
 */
function canonHolding(h, customEntry) {
  return rowToHolding(holdingToRow(h, customEntry))
}

/** The quick-add entry a row carries, rebuilt, or null for a catalog note. */
function rowToCustomEntry(row) {
  if (!row?.custom_entry) return null
  return jsonToCustomEntry(row.custom_entry, textIn(row.catalog_sid))
}

/** `CUSTOM` (an array, keyed by nothing) → a lookup by sid. */
function indexCustom(entries) {
  const out = {}
  for (const e of entries ?? []) if (e?.sid) out[e.sid] = e
  return out
}

/**
 * The whole vault.
 *
 * Array order is the client's; the table has none, so a read orders by
 * `created_at` instead — which is why the RPC never resets it. See
 * BUILD_NOTES on the "added" sort.
 */
function vaultToRows(collection, customBySid = {}) {
  return (collection ?? []).map((h) => holdingToRow(h, customBySid?.[h?.catId]))
}

/**
 * Rows → both of the app's stores at once.
 *
 * Returns `{collection, custom}` rather than just the collection,
 * because reconstructing one without the other is precisely the bug the
 * `custom_entry` column exists to make impossible.
 */
function rowsToVault(rows) {
  const collection = []
  const bySid = {}
  for (const row of rows ?? []) {
    collection.push(rowToHolding(row))
    const entry = rowToCustomEntry(row)
    if (entry) bySid[entry.sid] = entry
  }
  return { collection, custom: Object.values(bySid) }
}

/* ── the profile ───────────────────────────────────────────────────── */

/*
 * `notevault:profile` is `{username, isPublic, usernameSetAt}` and maps
 * onto the caller's own `profiles` row. No RPC: it is one row and one
 * PATCH, and RLS already scopes it to auth.uid().
 *
 * ⚠ THIS ROUND TRIP IS ASYMMETRIC ON PURPOSE, and it is the one place
 * where what comes back is deliberately NOT what went in.
 *
 * `username_set_at` is the clock behind the one-change-per-year lock,
 * and db/001's `username_lock` trigger maintains it server-side: it
 * stamps now() on a real change and copies the old value forward on
 * every other update, OVERWRITING whatever the client sent. So a
 * hostile PATCH of that column returns HTTP 200 and moves nothing.
 *
 * `profileToRow` therefore does not send it at all. Sending a value
 * that is guaranteed to be discarded would suggest the client has a say
 * in when its own lock expires, and it does not — which is the entire
 * reason the column has a trigger in front of it.
 */
function profileToRow(p) {
  return {
    username: textOut(p?.username),
    is_public: Boolean(p?.isPublic),
  }
}

function rowToProfile(row) {
  return {
    username: textIn(row?.username),
    isPublic: Boolean(row?.is_public),
    /* timestamptz → epoch ms, because username.js:15,21,34 do arithmetic
     * against Date.now(). 0 rather than null: the app's initial state is
     * `usernameSetAt: 0` and `Date.now() - 0` is a very long time ago,
     * which is exactly "no lock is armed". */
    usernameSetAt: row?.username_set_at ? Date.parse(row.username_set_at) : 0,
  }
}

export {
  CUSTOM_SID_PREFIX,
  HOLDING_KEYS,
  /* `PHOTO_SCHEME` and `SLOTS` were exported from here and left with
   * the grammar in Phase 4 Step 3. Nothing imported either — checked
   * across src/ and scripts/ before removing them, rather than kept
   * "just in case", which is how a module keeps a surface it no longer
   * has an implementation for. `IDB_SLOTS` lives in shared/photo-ref.js
   * if a caller ever needs it. */
  canonHolding,
  customEntryToJson,
  holdingToRow,
  indexCustom,
  isCustomSid,
  isUuid,
  jsonToCustomEntry,
  newHoldingId,
  parsePhotoRef,
  photoRef,
  profileToRow,
  rowToCustomEntry,
  rowToHolding,
  rowToProfile,
  rowsToVault,
  /* Exported for src/remote.js's community read (Phase 3 Step 6), which
   * turns `public_holdings` rows into the note shape community.js:18
   * builds. The NULL → "" rule belongs in one place; a second copy in
   * the backend would be two implementations of one decision, free to
   * drift. */
  textIn,
  vaultToRows,
}
